import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import { Button } from "../ui/button";
import { STRIPE_EVENTS } from "../../constants/analyticsEvents";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { useAction } from "convex/react";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

export default function StripePaymentElement() {
  const stripe = useStripe();
  const elements = useElements();
  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));

  const connectUserStripeAccount = useAction(
    api.stripe.connectUserStripeAccount,
  );

  const [errorMessage, setErrorMessage] = useState();
  const [loading, setLoading] = useState(false);
  const { track } = useMixpanel();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleError = (error: any) => {
    setLoading(false);
    setErrorMessage(error.message);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements || !user) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setLoading(true);

    // Track initiation
    track(STRIPE_EVENTS.PAYMENT_METHOD_SETUP_INITIATED, {
      user_id: user._id,
      email: user.email,
      timestamp: new Date().toISOString(),
      currency: "usd",
    });

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      track(STRIPE_EVENTS.PAYMENT_METHOD_SETUP_FAILED, {
        user_id: user._id,
        error_message: submitError.message,
        error_type: submitError.type,
        timestamp: new Date().toISOString(),
        stage: "form_validation",
      });
      handleError(submitError);
      return;
    }

    // Create the SetupIntent and obtain clientSecret
    const clientSecret = await connectUserStripeAccount({
      currency: "usd",
      userId: user._id,
    });

    if (!clientSecret) {
      track(STRIPE_EVENTS.PAYMENT_METHOD_SETUP_FAILED, {
        user_id: user._id,
        error_message: "Failed to connect Stripe account",
        timestamp: new Date().toISOString(),
        stage: "setup_intent_creation",
      });
      handleError(new Error("Failed to connect Stripe account"));
      return;
    }

    // Confirm the SetupIntent using the details collected by the Payment Element
    const { error } = await stripe.confirmSetup({
      elements,
      clientSecret,
      confirmParams: {
        return_url: `${window.location.origin}/dashboard/settings/billing`,
      },
    });

    if (error) {
      // This point is only reached if there's an immediate error when
      // confirming the setup. Show the error to your customer (for example, payment details incomplete)
      track(STRIPE_EVENTS.PAYMENT_METHOD_SETUP_FAILED, {
        user_id: user._id,
        error_message: error.message,
        error_type: error.type,
        error_code: error.code,
        timestamp: new Date().toISOString(),
        stage: "setup_confirmation",
      });
      handleError(error);
    } else {
      // Your customer is redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer is redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
      track(STRIPE_EVENTS.PAYMENT_METHOD_SETUP_COMPLETED, {
        user_id: user._id,
        timestamp: new Date().toISOString(),
        currency: "usd",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <p className="text-sm text-muted-foreground mt-2">
        Please toggle "Save payment details for future purchases" to save your
        payment method.
      </p>
      <Button
        type="submit"
        disabled={loading || !stripe || !elements}
        className="w-full mt-4"
      >
        {loading ? "Connecting..." : "Add Payment Method"}
      </Button>
      {errorMessage && <div className="text-red-500">{errorMessage}</div>}
    </form>
  );
}
